import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { mockBooks } from '../data/mockBooks';
import { Haptics, NotificationType } from '@capacitor/haptics';

const BookContext = createContext();

export const useBooks = () => useContext(BookContext);

export const BookProvider = ({ children }) => {
  const { user, profile, refreshProfile } = useAuth();
  const [books, setBooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [brewHistory, setBrewHistory] = useState([]);
  const [brewLoading, setBrewLoading] = useState(false);
  const [loading, setLoading] = useState(true);


  // One-time merge of guest-mode data (created on this device before signing in)
  // into the signed-in account. Guest data lives in the my-reading-* localStorage
  // keys; the keys are only cleared after the books insert succeeds, so a failed
  // import can always be retried on the next load.
  const migrateGuestData = async (accountBooks, accountLogs, accountBrews) => {
    if (localStorage.getItem('guest-merge-declined')) return;
    let guestBooks = [];
    let guestLogs = [];
    let guestBrews = [];
    try {
      guestBooks = JSON.parse(localStorage.getItem('my-reading-books')) || [];
      guestLogs = JSON.parse(localStorage.getItem('my-reading-logs')) || [];
      guestBrews = JSON.parse(localStorage.getItem('my-brew-history')) || [];
    } catch (e) {
      return;
    }
    if (guestBooks.length === 0 && guestLogs.length === 0 && guestBrews.length === 0) return;

    const summary = guestBooks.length === 1 ? '1 book' : `${guestBooks.length} books`;
    if (!window.confirm(`We found ${summary} saved on this device from before you signed in. Add them to your sanctuary?`)) {
      localStorage.setItem('guest-merge-declined', '1');
      return;
    }

    const keyOf = (b) => (b.isbn ? `isbn:${b.isbn}` : `t:${(b.title || '').toLowerCase()}|${(b.author || '').toLowerCase()}`);
    const existingByKey = {};
    accountBooks.forEach(b => { existingByKey[keyOf(b)] = b.id; });

    const idMap = {}; // guest book id -> account book id
    const toInsert = [];
    guestBooks.forEach(b => {
      const match = existingByKey[keyOf(b)];
      if (match) {
        idMap[b.id] = match;
      } else {
        toInsert.push(b);
      }
    });

    let insertedBooks = [];
    if (toInsert.length > 0) {
      const rows = toInsert.map(({ id, created_at, user_id, ...rest }) => ({ ...rest, user_id: user.id }));
      const { data, error } = await supabase.from('books').insert(rows).select();
      if (error) {
        console.error('Guest book merge failed:', error);
        alert('Could not import your offline books right now. They are still saved on this device - please try again later.');
        return;
      }
      insertedBooks = data || [];
      insertedBooks.forEach((row, i) => { idMap[toInsert[i].id] = row.id; });
    }

    // Reading logs: remap to account book ids; logs for books that never made it stay behind.
    const logRows = guestLogs
      .filter(l => idMap[l.book_id])
      .map(({ id, created_at, user_id, book_id, ...rest }) => ({ ...rest, book_id: idMap[book_id], user_id: user.id }));
    let insertedLogs = [];
    if (logRows.length > 0) {
      const { data, error } = await supabase.from('reading_logs').insert(logRows).select();
      if (!error) insertedLogs = data || [];
      else console.error('Guest log merge failed:', error);
    }

    // Brew check-ins: only dates the account doesn't already have.
    const haveDates = new Set(accountBrews.map(b => b.check_in_date));
    const brewRows = guestBrews
      .filter(b => b.check_in_date && !haveDates.has(b.check_in_date))
      .map(({ id, created_at, user_id, ...rest }) => ({ ...rest, user_id: user.id }));
    let insertedBrews = [];
    if (brewRows.length > 0) {
      const { data, error } = await supabase.from('brew_history').insert(brewRows).select();
      if (!error) insertedBrews = data || [];
      else console.error('Guest brew merge failed:', error);
    }

    // Archive rather than delete, so a discrepancy report can be checked
    // against the exact pre-merge data (see 186-vs-122 incident, 2026-07-02).
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    localStorage.setItem(`my-reading-books-imported-${stamp}`, JSON.stringify(guestBooks));
    localStorage.setItem(`my-reading-logs-imported-${stamp}`, JSON.stringify(guestLogs));
    localStorage.setItem(`my-brew-history-imported-${stamp}`, JSON.stringify(guestBrews));
    console.info(`Guest merge: ${guestBooks.length} local book(s) found, ${toInsert.length} inserted, ${guestBooks.length - toInsert.length} deduped onto existing account books; archived under my-reading-books-imported-${stamp}`);
    localStorage.removeItem('my-reading-books');
    localStorage.removeItem('my-reading-logs');
    localStorage.removeItem('my-brew-history');
    localStorage.removeItem('guest-merge-declined');

    if (insertedBooks.length > 0) setBooks(prev => [...insertedBooks, ...prev]);
    if (insertedLogs.length > 0) setLogs(prev => [...insertedLogs, ...prev]);
    if (insertedBrews.length > 0) {
      setBrewHistory(prev => [...insertedBrews, ...prev]);
      if (refreshProfile) refreshProfile();
    }
    alert(`Imported ${insertedBooks.length} book(s), ${insertedLogs.length} reading log(s) and ${insertedBrews.length} brew check-in(s) from this device.`);
  };

  // Load data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (user) {
        const { data: booksData, error: booksError } = await supabase
          .from('books')
          .select('*')
          .order('created_at', { ascending: false });

        const { data: logsData, error: logsError } = await supabase
          .from('reading_logs')
          .select('*')
          .order('date', { ascending: false });

        const { data: brewData, error: brewError } = await supabase
          .from('brew_history')
          .select('*')
          .order('check_in_date', { ascending: false });

        if (!booksError) setBooks(booksData || []);
        if (!logsError) setLogs(logsData || []);
        if (!brewError) setBrewHistory(brewData || []);

        if (!booksError) {
          await migrateGuestData(booksData || [], logsData || [], brewData || []);
        }
      } else {
        const savedBooks = localStorage.getItem('my-reading-books');
        const savedLogs = localStorage.getItem('my-reading-logs');
        const savedBrews = localStorage.getItem('my-brew-history');
        setBooks(savedBooks ? JSON.parse(savedBooks) : []);
        setLogs(savedLogs ? JSON.parse(savedLogs) : []);
        setBrewHistory(savedBrews ? JSON.parse(savedBrews) : []);
      }
      setLoading(false);
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    if (!user) {
      localStorage.setItem('my-reading-books', JSON.stringify(books));
      localStorage.setItem('my-reading-logs', JSON.stringify(logs));
      localStorage.setItem('my-brew-history', JSON.stringify(brewHistory));
    }
  }, [books, logs, brewHistory, user]);

  const calculateLocalStreak = (history) => {
    if (!history || history.length === 0) return 0;
    const dates = [...new Set(history.map(h => h.check_in_date))].sort().reverse();
    const todayStr = new Date().toISOString().split('T')[0];
    const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    
    if (dates[0] !== todayStr && dates[0] !== yesterdayStr) {
      return 0;
    }
    
    let streak = 1;
    for (let i = 0; i < dates.length - 1; i++) {
      const current = new Date(dates[i]);
      const next = new Date(dates[i + 1]);
      const diff = Math.round((current - next) / (1000 * 60 * 60 * 24));
      if (diff === 1) {
        streak++;
      } else if (diff > 1) {
        break;
      }
    }
    return streak;
  };

  const streakCount = user ? (profile?.brew_streak || 0) : calculateLocalStreak(brewHistory);
  const highestStreakCount = user ? (profile?.highest_brew_streak || 0) : streakCount;
  const todayBrew = brewHistory.some(h => h.check_in_date === new Date().toISOString().split('T')[0]);
  // profile.brew_streak is a DB-stored counter only updated on the next check-in insert,
  // so it stays frozen (never quietly prunes) once a user stops checking in. Ambient
  // decorations that visually depend on "is the streak alive right now" (e.g. the header
  // vine) should use this date-aware recompute instead, which already treats a lapsed
  // streak (no check-in today or yesterday) as 0.
  const currentStreakCount = calculateLocalStreak(brewHistory);

  const checkInBrew = async (mugStyle = 'clay') => {
    setBrewLoading(true);
    const todayStr = new Date().toISOString().split('T')[0];
    
    if (user) {
      const { data, error } = await supabase
        .from('brew_history')
        .insert([{ user_id: user.id, check_in_date: todayStr, mug_style: mugStyle }])
        .select();
      
      if (!error && data) {
        setBrewHistory(prev => [data[0], ...prev]);
        if (refreshProfile) refreshProfile();
      } else {
        console.error("Brew check-in error:", error);
      }
    } else {
      const newCheckin = {
        id: Date.now().toString(),
        check_in_date: todayStr,
        mug_style: mugStyle
      };
      setBrewHistory(prev => [newCheckin, ...prev]);
    }
    setBrewLoading(false);
  };

  const addBook = async (book) => {
    const dupe = books.find(b =>
      (b.title || '').trim().toLowerCase() === (book.title || '').trim().toLowerCase() &&
      (b.author || '').trim().toLowerCase() === (book.author || '').trim().toLowerCase()
    );
    if (dupe && !window.confirm(`"${book.title}" is already on your shelf (${dupe.status}). Add a duplicate copy anyway?`)) {
      return;
    }
    if (user) {
      const { data, error } = await supabase
        .from('books')
        .insert([{ ...book, user_id: user.id }])
        .select();
      if (!error && data) {
        setBooks(prev => [data[0], ...prev]);
      }
    } else {
      setBooks(prev => [{ ...book, id: Date.now().toString() }, ...prev]);
    }
  };

  const addLog = async (log) => {
    if (user) {
      const { data, error } = await supabase
        .from('reading_logs')
        .insert([{ ...log, user_id: user.id }])
        .select();
      if (!error && data) {
        setLogs(prev => [data[0], ...prev]);
      }
    } else {
      setLogs(prev => [{ ...log, id: Date.now().toString() }, ...prev]);
    }
  };

  const updateBook = async (id, updatedFields) => {
    if (updatedFields.status === 'read') {
      try {
        await Haptics.notification({ type: NotificationType.Success });
      } catch (e) {}
    }

    if (user) {
      const { error } = await supabase
        .from('books')
        .update(updatedFields)
        .eq('id', id);
      if (!error) {
        setBooks(prev => prev.map(book => book.id === id ? { ...book, ...updatedFields } : book));
      }
    } else {
      setBooks(prev => prev.map(book => book.id === id ? { ...book, ...updatedFields } : book));
    }
  };

  const deleteBook = async (id) => {
    if (user) {
      const { error } = await supabase.from('books').delete().eq('id', id);
      if (!error) setBooks(prev => prev.filter(book => book.id !== id));
    } else {
      setBooks(prev => prev.filter(book => book.id !== id));
    }
  };

  // The Favorites Garden: personal rank persistence (fixes a previously-missing
  // function -- RankingFlow.js called rankBook() but it never existed, so
  // finishing a comparison silently failed for every user).
  const rankBook = async (id, rank) => {
    await updateBook(id, { rank });
  };

  // Resolves a best-effort ISBN for duel logging when the book itself has none
  // (mirrors the book_cache title-lookup pattern used in BookDetailModal.js).
  const resolveDuelIsbn = async (book) => {
    if (book.isbn) return book.isbn;
    const matches = await searchCachedBooks(book.title);
    const match = matches.find(m => m.isbn);
    return match ? match.isbn : null;
  };

  // Logs one head-to-head comparison event for The Favorites Garden aggregation.
  // Fire-and-forget from the caller's perspective -- a logging failure should
  // never block the ranking flow itself.
  const logDuel = async (winner, loser) => {
    if (!user) return; // guest mode: no account to attribute the duel to
    try {
      const [winnerIsbn, loserIsbn] = await Promise.all([
        resolveDuelIsbn(winner),
        resolveDuelIsbn(loser),
      ]);
      if (!winnerIsbn || !loserIsbn) return; // can't aggregate without both ISBNs
      await supabase.from('book_duels').insert({
        user_id: user.id,
        winner_isbn: winnerIsbn,
        winner_title: winner.title,
        winner_author: winner.author,
        loser_isbn: loserIsbn,
        loser_title: loser.title,
        loser_author: loser.author,
      });
    } catch (e) {
      console.error('Failed to log book duel', e);
    }
  };

  // CACHE LOGIC
  const searchCachedBooks = async (query) => {
    const { data, error } = await supabase
      .from('book_cache')
      .select('*')
      .or(`title.ilike.%${query}%,author.ilike.%${query}%`)
      .limit(5);
    return error ? [] : data;
  };

  const searchCachedByGenre = async (genreHint) => {
    const { data, error } = await supabase
      .from('book_cache')
      .select('*')
      .ilike('genre', `%${genreHint}%`)
      .limit(10);
    return error ? [] : data;
  };

  const lookupCachedISBN = async (isbn) => {
    const { data, error } = await supabase
      .from('book_cache')
      .select('*')
      .eq('isbn', isbn)
      .maybeSingle();
    return error ? null : data;
  };

  const saveToCache = async (book) => {
    await supabase.from('book_cache').upsert([book], { onConflict: 'external_id' });
  };

  const loadSampleData = async () => {
    if (window.confirm("Add sample data?")) {
      if (user) {
        const booksToInsert = mockBooks.map(b => ({ ...b, id: undefined, user_id: user.id }));
        const { data } = await supabase.from('books').insert(booksToInsert).select();
        if (data) setBooks(prev => [...data, ...prev]);
      } else {
        setBooks(mockBooks);
      }
    }
  };

  const clearLibrary = async () => {
    if (window.confirm("Clear library?")) {
      if (user) {
        await supabase.from('books').delete().eq('user_id', user.id);
        await supabase.from('reading_logs').delete().eq('user_id', user.id);
      }
      setBooks([]);
      setLogs([]);
    }
  };

  return (
    <BookContext.Provider value={{ 
      books, logs, loading, addBook, addLog, updateBook, deleteBook, 
      searchCachedBooks, searchCachedByGenre, lookupCachedISBN, saveToCache,
      loadSampleData, clearLibrary,
      brewHistory, todayBrew, brewLoading, checkInBrew, streakCount, highestStreakCount, currentStreakCount,
      rankBook, logDuel
    }}>
      {children}
    </BookContext.Provider>
  );
};
